import React, { useState, useEffect } from 'react';
import { Box, Typography, Card, Stack, Avatar, Button as MuiButton, TextField, Divider, Switch, MenuItem, Alert, CircularProgress, Grid } from '@mui/material';
import { supabase } from '../lib/supabase';
import { useAppContext } from '../context/AppContext';
import { MapPin, Camera, Instagram, Twitter, Linkedin, ChevronRight, PenSquare, Search, Bell, HelpCircle, User } from 'lucide-react';

export default function Profile() {
    const { user } = useAppContext();

    // Local state for profile edits
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [bio, setBio] = useState('');
    const [location, setLocation] = useState('');
    const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);

    useEffect(() => {
        if (!user) return;

        const fetchProfile = async () => {
            try {
                const { data, error } = await supabase
                    .from('profiles')
                    .select('*')
                    .eq('id', user.id)
                    .single();

                if (error) throw error;

                if (data) {
                    setName(data.full_name || user.name || '');
                    setEmail(user.email || '');
                    setBio(data.bio || '');
                    setLocation(data.location || '');
                    setAvatarUrl(data.avatar_url || null);
                }
            } catch (err: any) {
                console.error('Error fetching profile:', err.message);
            } finally {
                setLoading(false);
            }
        };

        fetchProfile();
    }, [user]);

    const handleAvatarUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
        try {
            setUploading(true);
            if (!event.target.files || event.target.files.length === 0) {
                throw new Error('You must select an image to upload.');
            }

            const file = event.target.files[0];
            const fileExt = file.name.split('.').pop();
            const fileName = `${user?.id}-${Math.random()}.${fileExt}`;
            const filePath = `${fileName}`;

            let { error: uploadError } = await supabase.storage
                .from('avatars')
                .upload(filePath, file);

            if (uploadError) throw uploadError;

            const { data: { publicUrl } } = supabase.storage
                .from('avatars')
                .getPublicUrl(filePath);

            setAvatarUrl(publicUrl);

            // Immediately update profile with new avatar
            await supabase
                .from('profiles')
                .update({ avatar_url: publicUrl })
                .eq('id', user?.id);

            setMessage({ type: 'success', text: 'Avatar updated!' });
        } catch (error: any) {
            setMessage({ type: 'error', text: error.message });
        } finally {
            setUploading(false);
        }
    };

    const handleSave = async () => {
        if (!user) return;
        setSaving(true);
        setMessage(null);

        try {
            const { error } = await supabase
                .from('profiles')
                .update({
                    full_name: name,
                    bio,
                    location,
                    avatar_url: avatarUrl,
                    updated_at: new Date().toISOString(),
                })
                .eq('id', user.id);

            if (error) throw error;
            setMessage({ type: 'success', text: 'Profile updated successfully!' });
        } catch (err: any) {
            setMessage({ type: 'error', text: err.message });
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', minHeight: '60vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ width: '100%', maxWidth: 1000, mx: 'auto' }}>
            {message && (
                <Alert severity={message.type} sx={{ mb: 4, borderRadius: 2 }}>
                    {message.text}
                </Alert>
            )}

            {/* Top Main Section - Profile Card */}
            <Card elevation={0} sx={{ p: 4, borderRadius: 3, boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)', border: '1px solid #e2e8f0', mb: 4, bgcolor: 'white' }}>
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'center', sm: 'center' }, gap: 3 }}>
                    <Box sx={{ position: 'relative' }}>
                        <Box sx={{ width: 100, height: 100, borderRadius: 3, overflow: 'hidden', bgcolor: '#f1f5f9' }}>
                            {uploading ? (
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                                    <CircularProgress size={24} />
                                </Box>
                            ) : (
                                <img
                                    src={avatarUrl || `https://ui-avatars.com/api/?name=${name.replace(' ', '+')}&background=1e293b&color=fff&size=200`}
                                    alt={name}
                                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                />
                            )}
                        </Box>
                        <input
                            type="file"
                            accept="image/*"
                            id="avatar-upload"
                            style={{ display: 'none' }}
                            onChange={handleAvatarUpload}
                            disabled={uploading}
                        />
                        <label htmlFor="avatar-upload">
                            <Box sx={{
                                position: 'absolute', bottom: -8, right: -8,
                                bgcolor: '#4f46e5', color: 'white',
                                borderRadius: '50%', p: 0.75, border: '2px solid white',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                cursor: 'pointer', '&:hover': { bgcolor: '#4338ca' }
                            }}>
                                <Camera size={14} />
                            </Box>
                        </label>
                    </Box>

                    <Box sx={{ flexGrow: 1, textAlign: { xs: 'center', sm: 'left' } }}>
                        <Typography variant="h5" fontWeight={800} color="#111827">
                            {name}
                        </Typography>
                        <Typography variant="body2" color="#64748b" gutterBottom>
                            {email}
                        </Typography>
                        <Stack direction="row" spacing={1} justifyContent={{ xs: 'center', sm: 'flex-start' }} mt={1}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 1.5, py: 0.5, bgcolor: '#f1f5f9', borderRadius: 2 }}>
                                <MapPin size={12} color="#64748b" />
                                <Typography variant="caption" fontWeight={600} color="#64748b">{location || 'Set location'}</Typography>
                            </Box>
                        </Stack>
                    </Box>

                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <MuiButton
                            variant="outlined"
                            disabled={saving}
                            onClick={() => window.location.reload()}
                            sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2, borderColor: '#e2e8f0', color: '#111827' }}
                        >
                            Cancel
                        </MuiButton>
                        <MuiButton
                            variant="contained"
                            onClick={handleSave}
                            disabled={saving || uploading}
                            sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2, bgcolor: '#4f46e5', '&:hover': { bgcolor: '#4338ca' } }}
                        >
                            {saving ? 'Saving...' : 'Save Changes'}
                        </MuiButton>
                    </Box>
                </Box>
            </Card>

            <Grid container spacing={4}>
                {/* Left Column - Personal Info */}
                <Grid size={{ xs: 12, md: 8 }}>
                    <Card elevation={0} sx={{ p: 4, borderRadius: 3, border: '1px solid #e2e8f0', bgcolor: 'white' }}>
                        <Typography variant="h6" fontWeight={700} color="#111827" mb={3}>
                            Personal Information
                        </Typography>

                        <Grid container spacing={3}>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <Typography variant="body2" fontWeight={600} color="#334155" mb={1}>
                                    Full Name
                                </Typography>
                                <TextField
                                    fullWidth
                                    variant="outlined"
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    placeholder="Enter your full name"
                                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <Typography variant="body2" fontWeight={600} color="#334155" mb={1}>
                                    Email Address
                                </Typography>
                                <TextField
                                    fullWidth
                                    variant="outlined"
                                    value={email}
                                    disabled
                                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#f8fafc' } }}
                                />
                            </Grid>
                            <Grid size={12}>
                                <Typography variant="body2" fontWeight={600} color="#334155" mb={1}>
                                    Bio
                                </Typography>
                                <TextField
                                    fullWidth
                                    variant="outlined"
                                    multiline
                                    rows={4}
                                    value={bio}
                                    onChange={(e) => setBio(e.target.value)}
                                    placeholder="Tell us a bit about yourself..."
                                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <Typography variant="body2" fontWeight={600} color="#334155" mb={1}>
                                    Location
                                </Typography>
                                <TextField
                                    fullWidth
                                    variant="outlined"
                                    value={location}
                                    onChange={(e) => setLocation(e.target.value)}
                                    placeholder="City, Country"
                                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                />
                            </Grid>
                        </Grid>
                    </Card>

                    <Card elevation={0} sx={{ p: 4, borderRadius: 3, border: '1px solid #e2e8f0', bgcolor: 'white', mt: 4 }}>
                        <Typography variant="h6" fontWeight={700} color="#111827" mb={3}>
                            Social Profiles
                        </Typography>
                        <Stack spacing={2}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Instagram size={20} color="#64748b" />
                                <TextField fullWidth size="small" placeholder="Instagram Username" sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Twitter size={20} color="#64748b" />
                                <TextField fullWidth size="small" placeholder="Twitter Username" sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Linkedin size={20} color="#64748b" />
                                <TextField fullWidth size="small" placeholder="LinkedIn Profile" sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
                            </Box>
                        </Stack>
                    </Card>
                </Grid>

                {/* Right Column - Preferences */}
                <Grid size={{ xs: 12, md: 4 }}>
                    <Card elevation={0} sx={{ p: 4, borderRadius: 3, border: '1px solid #e2e8f0', bgcolor: 'white' }}>
                        <Typography variant="subtitle1" fontWeight={700} color="#111827" mb={3}>
                            App Preferences
                        </Typography>

                        <Stack spacing={3}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Box>
                                    <Typography variant="body2" fontWeight={600} color="#334155">
                                        Email Notifications
                                    </Typography>
                                    <Typography variant="caption" color="#64748b">
                                        Receive weekly generation reports
                                    </Typography>
                                </Box>
                                <Switch defaultChecked sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#4f46e5' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#4f46e5' } }} />
                            </Box>

                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Box>
                                    <Typography variant="body2" fontWeight={600} color="#334155">
                                        Usage Alerts
                                    </Typography>
                                    <Typography variant="caption" color="#64748b">
                                        Notify when credits are low
                                    </Typography>
                                </Box>
                                <Switch defaultChecked sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#4f46e5' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#4f46e5' } }} />
                            </Box>

                            <Divider />

                            <Box>
                                <Typography variant="body2" fontWeight={600} color="#334155" mb={1}>
                                    Language
                                </Typography>
                                <TextField
                                    select
                                    fullWidth
                                    size="small"
                                    defaultValue="English (US)"
                                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                >
                                    <MenuItem value="English (US)">English (US)</MenuItem>
                                    <MenuItem value="Spanish">Spanish</MenuItem>
                                    <MenuItem value="French">French</MenuItem>
                                </TextField>
                            </Box>
                        </Stack>

                        <Divider sx={{ my: 4 }} />

                        <MuiButton
                            fullWidth
                            variant="outlined"
                            color="error"
                            sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2, borderColor: '#fee2e2', color: '#ef4444', '&:hover': { bgcolor: '#fef2f2', borderColor: '#fecaca' } }}
                        >
                            Delete Account
                        </MuiButton>
                    </Card>

                    <Card elevation={0} sx={{ p: 4, borderRadius: 3, bgcolor: '#f8fafc', border: '1px solid #f1f5f9', mt: 4 }}>
                        <Typography variant="subtitle2" fontWeight={700} color="#1e293b" mb={2}>
                            Need help?
                        </Typography>
                        <Stack spacing={2}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, textDecoration: 'none', color: '#4b5563', '&:hover': { color: '#4f46e5' }, cursor: 'pointer' }}>
                                <Bell size={16} />
                                <Typography variant="body2" fontWeight={500}>Notification Settings</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, textDecoration: 'none', color: '#4b5563', '&:hover': { color: '#4f46e5' }, cursor: 'pointer' }}>
                                <HelpCircle size={16} />
                                <Typography variant="body2" fontWeight={500}>Help Center</Typography>
                            </Box>
                        </Stack>
                    </Card>
                </Grid>
            </Grid>
        </Box>
    );
}
